import cn from 'classnames';
import { AiOutlineDelete, AiOutlineEdit, AiOutlineEye } from 'react-icons/ai';
import { GoLink, GoUnlink } from 'react-icons/go';
import { IoPersonAddOutline, IoPersonOutline } from 'react-icons/io5';
import { MdCancel, MdCheckCircle } from 'react-icons/md';
import { TbRestore } from 'react-icons/tb';
import { Card } from '../../../../../../components/card/card';
import { Chip } from '../../../../../../components/chip/chip';
import { IconButton } from '../../../../../../components/icon-button/icon-button';
import { HasAccess } from '../../../../../../features/has-access/has-access';
import { Table } from '../../../../../../features/table/table';
import { TableFilterDateTimeRange } from '../../../../../../features/table/table-filter/table-filter-date-time-range/table-filter-date-time-range';
import { TableFilterInput } from '../../../../../../features/table/table-filter/table-filter-input/table-filter-input';
import { TableFilterMultiSelect } from '../../../../../../features/table/table-filter/table-filter-multi-select/table-filter-multi-select';
import { TableFilterSingleSelect } from '../../../../../../features/table/table-filter/table-filter-single-select/table-filter-single-select';
import { formatRuDateString } from '../../../../../../helpers/date-time';
import { useEmployeesUtils } from '../use-employees-utils';
import s from './employees-table.module.css';

export const EmployeesTable = () => {
  const {
    getLocale,
    isLoadingFetch,
    staff,
    branches,
    t,
    tag,
    addQueryParams,
    pagination,
    createEmployeeModal,
    deleteEmployeeModal,
    qrEmployeeModal,
    unlinkEmployeeModal,
    navigateToProfile,
  } = useEmployeesUtils();

  return (
    <Card>
      <Table
        id="employee-list"
        isLoading={isLoadingFetch}
        columnSettings
        withArchive
        pagination={pagination}
        className={s.feature}
        onEmpty={{
          title: t('Не найдено ни одного сотрудника'),
          text: t('Добавьте сотрудника с помощью кнопки выше'),
        }}
        cols={{
          employee: {
            label: t('Сотрудник'),
            filter: (
              <TableFilterInput
                name="employee"
                suggestOptions={staff?.map(
                  (item) => `${getLocale(item.name)} ${getLocale(item.surname)}`,
                )}
              />
            ),
          },
          onShift: {
            label: t('На смене'),
            filter: (
              <TableFilterSingleSelect
                name="shift_state"
                options={[
                  {
                    id: 'ON_SHIFT',
                    value: t('На смене'),
                  },
                  {
                    id: 'OFF_SHIFT',
                    value: t('Не на смене'),
                  },
                ]}
              />
            ),
          },
          tags: {
            label: t('Должности'),
            filter: <TableFilterMultiSelect name="tags" options={tag} />,
          },
          branch: {
            label: t('Филиал'),
            filter: (
              <TableFilterMultiSelect
                name="place_ids"
                options={branches.map((item) => ({
                  id: item.id,
                  value: item.value,
                }))}
              />
            ),
          },
          createdAt: {
            label: t('Добавлен'),
            filter: <TableFilterDateTimeRange name="created_at" />,
          },
          actions: { label: t('Действия') },
        }}
        rows={staff?.map((item) => {
          const isProfileExist = !!getLocale(item.profile?.display_title);

          return {
            employee: (
              <span style={{ opacity: item.is_deleted ? 0.5 : 1 }}>{`${getLocale(
                item.name,
              )} ${getLocale(item.surname)}`}</span>
            ),
            onShift: (
              <div
                className={cn(
                  s.onlineStatus,
                  item.shift_state === 'ON_SHIFT' ? s.online : s.offline,
                )}
              >
                {item.shift_state === 'ON_SHIFT' ? <MdCheckCircle /> : <MdCancel />}
              </div>
            ),
            tags: (
              <ul className={s.chips}>
                {item.tags?.map((tag) => (
                  <Chip variant="info" size="md" key={tag.id}>
                    {tag.value}
                  </Chip>
                ))}
              </ul>
            ),
            branch: (
              <ul className={s.chips}>
                {item.places?.map((item, index) => (
                  <Chip variant="info" size="md" key={index}>
                    {getLocale(item.value)}
                  </Chip>
                ))}
              </ul>
            ),
            createdAt: formatRuDateString(item.created_at),
            actions: (
              <HasAccess service="staff" permissions={['write']}>
                <div className={s.actions}>
                  {item.is_deleted ? (
                    <IconButton
                      tooltip={t('Восстановить')}
                      variant="secondary"
                      size="sm"
                      onClick={() =>
                        addQueryParams({
                          restoreId: String(item.id),
                          restoreName: `${getLocale(item.name)} ${getLocale(item.surname)}`,
                        })
                      }
                    >
                      <TbRestore />
                    </IconButton>
                  ) : (
                    <>
                      {item.online ? (
                        <IconButton
                          tooltip={t('Разавторизовать')}
                          variant="secondary"
                          size="sm"
                          onClick={() =>
                            unlinkEmployeeModal.handleOpenModal({
                              id: item.id,
                              name: `${getLocale(item.name)} ${getLocale(item.surname)}`,
                            })
                          }
                        >
                          <GoUnlink />
                        </IconButton>
                      ) : (
                        <IconButton
                          tooltip={t('Авторизовать')}
                          variant="secondary"
                          size="sm"
                          onClick={() =>
                            qrEmployeeModal.handleOpenModal({
                              id: item.id,
                              staffName: `${getLocale(item.name)} ${getLocale(item.surname)}`,
                              type: 'login',
                            })
                          }
                        >
                          <AiOutlineEye />
                        </IconButton>
                      )}
                      <IconButton
                        tooltip={t('Зарегистрировать')}
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                          qrEmployeeModal.handleOpenModal({
                            id: item.id,
                            staffName: `${getLocale(item.name)} ${getLocale(item.surname)}`,
                            type: 'register',
                          })
                        }
                      >
                        <GoLink />
                      </IconButton>
                      <IconButton
                        variant="secondary"
                        size="sm"
                        tooltip={t(isProfileExist ? 'Профиль' : 'Создать профиль')}
                        onClick={() => navigateToProfile(item.id)}
                      >
                        {isProfileExist ? <IoPersonOutline /> : <IoPersonAddOutline />}
                      </IconButton>

                      <IconButton
                        tooltip={t('Редактировать')}
                        size="sm"
                        variant="secondary"
                        onClick={() => createEmployeeModal.handleOpenModal({ id: item.id })}
                      >
                        <AiOutlineEdit />
                      </IconButton>
                      <IconButton
                        tooltip={t('Удалить')}
                        size="sm"
                        variant="secondary"
                        onClick={() => deleteEmployeeModal.handleOpenModal({ id: item.id })}
                      >
                        <AiOutlineDelete />
                      </IconButton>
                    </>
                  )}
                </div>
              </HasAccess>
            ),
          };
        })}
      />
    </Card>
  );
};
